const MAX_MAILTO_LENGTH = 1900;

class B2BEnquiryComponent extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.errorEl = this.querySelector('.b2b-enquiry__error');
    this.fallback = this.querySelector('.b2b-enquiry__fallback');
    this.variants = this.#readVariants();
    this.lastBody = '';

    this.form?.addEventListener('submit', this.#onSubmit);
    this.querySelector('[data-copy]')?.addEventListener('click', this.#onCopy);
    window.addEventListener('popstate', this.#syncFacts);
    document.addEventListener('variant:update', this.#syncFacts);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#syncFacts);
    document.removeEventListener('variant:update', this.#syncFacts);
  }

  #readVariants() {
    const node = this.querySelector('script[data-variants]');
    try {
      return node ? JSON.parse(node.textContent) : {};
    } catch {
      return {};
    }
  }

  /** The variant picker keeps ?variant= in the URL; fall back to the first variant. */
  #currentVariant() {
    const id = new URL(window.location.href).searchParams.get('variant');
    if (id && this.variants[id]) return { id, ...this.variants[id] };
    const [firstId] = Object.keys(this.variants);
    return firstId ? { id: firstId, ...this.variants[firstId] } : null;
  }

  #syncFacts = () => {
    const sku = this.querySelector('[data-fact="sku"]');
    const variant = this.#currentVariant();
    if (sku && variant?.sku) sku.textContent = variant.sku;
  };

  #setError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  #validate() {
    let firstInvalid = null;
    for (const field of this.form.elements) {
      if (!(field instanceof HTMLInputElement) || !field.required) continue;
      const empty = !field.value.trim();
      const invalid = empty || (field.type === 'email' && !field.checkValidity());
      field.setAttribute('aria-invalid', String(invalid));
      if (invalid && !firstInvalid) firstInvalid = field;
    }
    if (firstInvalid) {
      this.#setError('Please complete the highlighted fields with a valid work email.');
      firstInvalid.focus();
      return false;
    }
    this.#setError('');
    return true;
  }

  #buildEnquiry(data) {
    const variant = this.#currentVariant();
    const showVariant = variant && variant.title && variant.title !== 'Default Title';
    const productName = showVariant
      ? `${this.dataset.productTitle} (${variant.title})`
      : this.dataset.productTitle;
    const productUrl =
      variant && Object.keys(this.variants).length > 1
        ? `${this.dataset.productUrl}?variant=${variant.id}`
        : this.dataset.productUrl;

    const subject = (this.dataset.subject || 'Trade enquiry: {product}').replace('{product}', productName);

    const lines = [
      `Product: ${productName}`,
      variant?.sku ? `SKU: ${variant.sku}` : '',
      `Link: ${productUrl}`,
      data.quantity ? `Quantity: ${data.quantity}` : '',
      '',
      `Name: ${data.name}`,
      data.company ? `Company: ${data.company}` : '',
      `Email: ${data.email}`,
      data.phone ? `Phone: ${data.phone}` : '',
      '',
      data.message ? `Requirements:\n${data.message}` : '',
    ];

    return { subject, body: lines.filter((line, i) => line || lines[i - 1]).join('\n').trim() };
  }

  #onSubmit = (event) => {
    event.preventDefault();
    if (!this.#validate()) return;

    const data = Object.fromEntries(
      [...new FormData(this.form)].map(([key, value]) => [key, String(value).trim()])
    );
    const { subject, body } = this.#buildEnquiry(data);
    this.lastBody = `To: ${this.dataset.recipient}\nSubject: ${subject}\n\n${body}`;

    const toMailto = (text) =>
      `mailto:${this.dataset.recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(text)}`;

    let href = toMailto(body);
    // Some mail clients drop mailto links that are too long; trim the free-text and say so.
    if (href.length > MAX_MAILTO_LENGTH) {
      const room = Math.max(0, body.length - (href.length - MAX_MAILTO_LENGTH));
      href = toMailto(`${body.slice(0, room)}…\n(message shortened - please paste the rest)`);
    }

    window.location.href = href;

    if (this.fallback) {
      const link = this.fallback.querySelector('[data-fallback-link]');
      if (link) link.href = href;
      this.fallback.hidden = false;
    }
  };

  #onCopy = async (event) => {
    const button = event.currentTarget;
    try {
      await navigator.clipboard.writeText(this.lastBody);
      button.textContent = 'copied';
    } catch {
      button.textContent = 'copy failed - select the message in your email app instead';
    }
  };
}

if (!customElements.get('b2b-enquiry-component')) {
  customElements.define('b2b-enquiry-component', B2BEnquiryComponent);
}
